import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { find, isNil } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DEFAULT_CREDIT_USAGE_HISTORY_DAYS, useSatelliteCreditUsageStats } from '@/api/credits';
import {
    PurchasableItem,
    SubscriptionOptionItem,
    useSubscriptionsInfo,
    useTelemetryCreditItems,
} from '@/api/subscriptions';
import { Timestamp } from '@/components/data/Timestamp';
import { GenericSkeleton } from '@/components/loaders/GenericSkeleton';
import { LoadingIndicatorIcon } from '@/components/loaders/LoadingIndicatorIcon';
import { SatCreditsQuantityEditor } from '@/components/transactions/SatCreditsQuantityEditor';
import { TransactionIntentFooter } from '@/components/transactions/TransactionIntentFooter';
import {
    TotalDueReadout,
    TransactionDescription,
    TransactionModal,
    TransactionSummary,
} from '@/components/transactions/TransactionModal';
import { InlineAlert, InlineAlertBody, InlineAlertPrimaryButton } from '@/components/ui/InlineAlert';
import { RECURRING_SATELLITE_CREDITS_PRICE_LOOKUP_KEY } from '@/config';
import {
    TransactionContextProvider,
    useTransactionContext,
    useTransactionContextLineItemQuantity,
} from '@/context/transaction';

const DEFAULT_RECURRING_CREDITS_QTY = 500;

export const SatCreditsRecurringTransaction = () => {
    return (
        <TransactionContextProvider
            type="subscription"
            prorationBehavior="none"
            defaultBackTarget="payment"
            totalDueLabel="Satellite credits cost due next invoice"
        >
            <RecurringTransactionModal />
        </TransactionContextProvider>
    );
};

function calcRecommendedQuantity(averageDailyUsageByActiveSpotters?: number) {
    if (!averageDailyUsageByActiveSpotters) {
        return undefined;
    }

    // 31 days at the current burn, rounded up to the nearest 100
    return Math.ceil((averageDailyUsageByActiveSpotters * 31) / 100) * 100;
}

export const RecurringTransactionModal = () => {
    const context = useTransactionContext('subscription');
    const { isPending, type, updateSubscription, mutationStatus } = context;

    const { data: subscriptionInfo, isPending: subscriptionPending } = useSubscriptionsInfo({
        config: { enabled: mutationStatus?.isIdle },
    });

    const { billingDate = undefined } = isPending || !(type === 'subscription') ? {} : context;
    const { data: creditItems } = useTelemetryCreditItems();
    const item = useMemo(
        () => find(creditItems, { price_lookup_key: RECURRING_SATELLITE_CREDITS_PRICE_LOOKUP_KEY }),
        [creditItems]
    );
    const { data: { averageDailyUsageByActiveSpotters } = {}, isPending: usagePending } =
        useSatelliteCreditUsageStats();

    const recommendedQuantity = calcRecommendedQuantity(averageDailyUsageByActiveSpotters);

    const upcomingItem = useMemo(
        () => subscriptionInfo?.data[0]?.items.find((l) => l.price_id === item?.price_id),
        [item?.price_id, subscriptionInfo?.data]
    );

    const { defaultQuantity, defaultQuantityLabel } = useMemo(() => {
        if (subscriptionPending || usagePending) {
            return {};
        }

        if (upcomingItem) {
            return { defaultQuantity: upcomingItem.quantity, defaultQuantityLabel: 'Current' };
        }
        if (recommendedQuantity) {
            return { defaultQuantity: recommendedQuantity, defaultQuantityLabel: 'Recommended' };
        }
        return { defaultQuantity: DEFAULT_RECURRING_CREDITS_QTY };
    }, [subscriptionPending, usagePending, upcomingItem, recommendedQuantity]);

    const noSubscription = !subscriptionPending && !subscriptionInfo?.data.length;

    return (
        <>
            <SubscriptionUpdateConfirmation item={item} />
            <TransactionModal>
                <TransactionDescription
                    title={upcomingItem ? 'Change recurring satellite credits' : 'Set up recurring satellite credits'}
                >
                    Credits will be added at the start of your next billing cycle
                    {!isNil(billingDate) && (
                        <>
                            {' '}
                            on <Timestamp value={billingDate} unit="date" />
                        </>
                    )}
                    . You will be automatically charged each month using your default payment method on your invoice
                    date.
                </TransactionDescription>
                <TransactionSummary>
                    {isNil(defaultQuantity) ? (
                        <LoadingIndicatorIcon isLoading={true} loadingSx={{ my: 4, mx: 'auto' }} />
                    ) : noSubscription ? (
                        <InlineAlert
                            sx={{ m: 2.5 }}
                            severity="warning"
                            actionButton={
                                <InlineAlertPrimaryButton href="/telemetry">Manage telemetry</InlineAlertPrimaryButton>
                            }
                        >
                            <InlineAlertBody>
                                Please enable some telemetry lines before setting up recurring credits
                            </InlineAlertBody>
                        </InlineAlert>
                    ) : (
                        <Stack>
                            <SatCreditsQuantityEditor
                                defaultQuantity={defaultQuantity}
                                defaultQuantityLabel={defaultQuantityLabel}
                                recommendedQuantity={recommendedQuantity}
                                item={item}
                            />
                            <Stack direction="row" sx={{ alignItems: 'center' }}>
                                {recommendedQuantity && (
                                    <Typography variant="body2" sx={{ py: 0.5 }}>
                                        Recommended credits based on usage by enabled Spotters over the past{' '}
                                        {DEFAULT_CREDIT_USAGE_HISTORY_DAYS} days.
                                    </Typography>
                                )}
                                {!!upcomingItem?.quantity && item && <CancelRecurringCreditsDialog item={item} />}
                            </Stack>
                        </Stack>
                    )}
                    <TotalDueReadout />
                </TransactionSummary>
                <TransactionIntentFooter onConfirm={() => updateSubscription()} />
            </TransactionModal>
        </>
    );
};

const CancelRecurringCreditsDialog = ({ item }: { item: SubscriptionOptionItem }) => {
    const {
        track,
        billingDate,
        updateSubscription,
        isUpdating: transactionUpdating,
    } = useTransactionContext('subscription');
    const [itemQty, setItemQty] = useTransactionContextLineItemQuantity(item.price_id);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isConfirmationPending, setConfirmationPending] = useState(false);

    const openDialog = () => {
        track('Cancel recurring credits clicked');
        setIsDialogOpen(true);
    };

    const closeDialog = () => {
        if (isConfirmationPending) {
            return;
        }

        track('Cancel recurring credits cancelled');
        setIsDialogOpen(false);
    };

    const onSubmit = async () => {
        track('Cancel recurring credits confirmed');
        setItemQty(0);
        setConfirmationPending(true);
    };

    /**
     * Effect: If we're trying to confirm and transaction is settled with quantity at zero, time to update.
     * We can close and let the SubscriptionUpdateConfirmation take over.
     */
    useEffect(() => {
        if (isConfirmationPending && !transactionUpdating && itemQty === 0) {
            updateSubscription();
            setConfirmationPending(false);
            setIsDialogOpen(false);
        }
    }, [isConfirmationPending, itemQty, transactionUpdating, updateSubscription]);

    return (
        <>
            <Button
                data-testid="cancel-recurring-credits-btn"
                variant="text"
                color="error"
                sx={{ ml: 'auto', mr: -1 }}
                onClick={openDialog}
            >
                Cancel recurring credits
            </Button>
            <Dialog
                open={isDialogOpen}
                onClose={closeDialog}
                aria-labelledby="cancel-recurring-credits-title"
                aria-describedby="cancel-recurring-credits-description"
            >
                <DialogTitle id="cancel-recurring-credits-title">
                    Are you sure you want to cancel your recurring satellite credits?
                </DialogTitle>
                <DialogContent>
                    <Typography id="cancel-recurring-credits-description">
                        Satellite credits will no longer be included in your monthly recurring invoice. This will take
                        effect on your next invoice
                        {!isNil(billingDate) && (
                            <>
                                {' '}
                                on <Timestamp value={billingDate} unit="date" />
                            </>
                        )}
                        . Your devices will still consume satellite credits when transmitting via satellite telemetry.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        disabled={isConfirmationPending}
                        variant="outlined"
                        onClick={closeDialog}
                        data-testid="cancel-subscription-dismiss-button"
                    >
                        No, go back
                    </Button>
                    <Button
                        disabled={isConfirmationPending}
                        variant="contained"
                        onClick={onSubmit}
                        color="error"
                        data-testid="cancel-subscription-confirm-button"
                    >
                        Yes, cancel recurring credits
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export const SubscriptionUpdateConfirmation = ({ item }: { item?: PurchasableItem }) => {
    const navigate = useNavigate();
    const {
        backTarget,
        billingDate,
        isPending: contextPending,
        mutationStatus: { error, isSuccess, isPending, isIdle, reset } = {},
    } = useTransactionContext('subscription');
    const [itemQty] = useTransactionContextLineItemQuantity(item?.price_id);

    if (contextPending || isIdle) {
        // ruling out both idle and context not ready
        return false;
    }

    const title = isSuccess ? 'Change successful' : error ? 'Change unsuccessful' : 'Change processing...';
    return (
        <Dialog open={true} onClose={() => {}}>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                {isPending && <GenericSkeleton lines={2} />}
                {error && <Alert severity="error">{error.message}</Alert>}
                {isSuccess &&
                    (itemQty === 0 ? (
                        <Typography>
                            Your recurring satellite credits have been cancelled. This will take effect starting on your
                            next invoice on <Timestamp value={billingDate} unit="date" />.
                        </Typography>
                    ) : (
                        <Typography>
                            Your recurring satellite credits have been saved. They will be added at the start of your
                            next billing cycle and you will be automatically charged using your default payment method
                            on your next invoice on <Timestamp value={billingDate} unit="date" />.
                        </Typography>
                    ))}
            </DialogContent>
            <DialogActions>
                {isSuccess && (
                    <>
                        {backTarget?.href !== '/payment' && (
                            <Button href="/payment" target="_blank" variant="text">
                                View payment settings
                            </Button>
                        )}
                        <Button onClick={() => navigate(backTarget?.href ?? '/')} autoFocus variant="contained">
                            Done
                        </Button>
                    </>
                )}
                {error && (
                    <Button onClick={() => reset!()} autoFocus variant="contained">
                        Back
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export const exportForTests = { calcRecommendedQuantity };
